import { useState } from 'react'
import { Link } from '@inertiajs/react'
import { ChevronDown, ChevronRight, Network, AlertTriangle, UserMinus, ListTodo } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

// "Mon équipe" — Sprint 5a / Unit 14a.
// Tree view of the actor's team subtree with three counts per node:
//   open       — open tasks (status in {pending, in_progress, on_hold})
//   overdue    — open AND due_at < now
//   unassigned — open AND assignee_user_id IS NULL
//
// Clicking a team deep-links into the task list filtered to that team.
// The tree is collapsible (controller-side `arrange_serializable` builds
// the children array; the page just toggles visibility). All counts are
// precomputed server-side via three GROUP BY aggregates — no per-node
// fetching from this view.

interface TeamCounts {
  open_count: number
  overdue_count: number
  unassigned_count: number
}

interface TeamNode extends TeamCounts {
  id: number
  name: string
  responsible_user: string | null
  depth: number
  tasks_path: string
  children: TeamNode[]
}

interface Totals {
  open: number
  overdue: number
  unassigned: number
}

interface Props {
  teams: TeamNode[]
  totals: Totals
  empty_perimeter: boolean
}

export default function TeamDashboardIndex({ teams, totals, empty_perimeter }: Props) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Mon équipe</h1>
          <p className="text-muted-foreground">
            Vue d'ensemble des tâches ouvertes dans votre périmètre d'équipe.
          </p>
        </div>
      </div>

      {empty_perimeter ? (
        <EmptyPerimeter />
      ) : (
        <>
          <TotalsCards totals={totals} />
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Hiérarchie d'équipes</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1">
                {teams.map((team) => (
                  <TeamTreeNode key={team.id} team={team} level={0} />
                ))}
              </ul>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

function TotalsCards({ totals }: { totals: Totals }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <StatCard
        icon={ListTodo}
        label="Tâches ouvertes"
        value={totals.open}
      />
      <StatCard
        icon={AlertTriangle}
        label="En retard"
        value={totals.overdue}
        emphasis={totals.overdue > 0}
      />
      <StatCard
        icon={UserMinus}
        label="Non assignées"
        value={totals.unassigned}
        emphasis={totals.unassigned > 0}
      />
    </div>
  )
}

function StatCard({
  icon: Icon,
  label,
  value,
  emphasis = false,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: number
  emphasis?: boolean
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-4">
        <Icon className={cn('h-5 w-5', emphasis ? 'text-destructive' : 'text-muted-foreground')} />
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p
            className={cn(
              'text-xl font-semibold tabular-nums',
              emphasis && 'text-destructive'
            )}
          >
            {value}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

function TeamTreeNode({ team, level }: { team: TeamNode; level: number }) {
  const [expanded, setExpanded] = useState(level < 1)
  const hasChildren = team.children.length > 0

  return (
    <li>
      <div
        className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent"
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-muted-foreground hover:text-foreground"
            aria-expanded={expanded}
            aria-label={expanded ? 'Réduire' : 'Développer'}
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <span className="inline-block w-4" aria-hidden="true" />
        )}

        <Network className="h-4 w-4 text-muted-foreground" />

        <Link href={team.tasks_path} className="font-medium hover:underline">
          {team.name}
        </Link>

        {team.responsible_user && (
          <span className="text-xs text-muted-foreground">· {team.responsible_user}</span>
        )}

        <div className="ml-auto flex items-center gap-2">
          <CountBadge label="Ouvertes" value={team.open_count} variant="secondary" />
          <CountBadge
            label="En retard"
            value={team.overdue_count}
            variant={team.overdue_count > 0 ? 'destructive' : 'outline'}
          />
          <CountBadge
            label="Non assignées"
            value={team.unassigned_count}
            variant={team.unassigned_count > 0 ? 'default' : 'outline'}
          />

          <Button asChild size="sm" variant="ghost">
            <Link href={team.tasks_path}>Voir</Link>
          </Button>
        </div>
      </div>

      {hasChildren && expanded && (
        <ul className="space-y-1">
          {team.children.map((child) => (
            <TeamTreeNode key={child.id} team={child} level={level + 1} />
          ))}
        </ul>
      )}

      {!hasChildren && team.open_count === 0 && level === 0 && (
        <p
          className="text-xs text-muted-foreground"
          style={{ paddingLeft: `${level * 16 + 36}px` }}
        >
          Aucune tâche ouverte
        </p>
      )}
    </li>
  )
}

function CountBadge({
  label,
  value,
  variant,
}: {
  label: string
  value: number
  variant: 'default' | 'destructive' | 'secondary' | 'outline'
}) {
  return (
    <Badge variant={variant} className="tabular-nums" title={label}>
      <span className="sr-only">{label}: </span>
      {value}
    </Badge>
  )
}

function EmptyPerimeter() {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Network className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <h2 className="text-base font-semibold">Aucune équipe assignée</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Contactez un administrateur pour rejoindre une équipe et accéder au tableau de bord.
        </p>
      </CardContent>
    </Card>
  )
}
